import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { API_BASE_URL } from '../config/api';
import './AdminComponents.css';
import { trackDeletedChat } from '../utils/analytics';

const MobileConversationViewer = () => {
  const { userId, conversationId } = useParams();
  const messagesEndRef = useRef(null);
  const { isAuthenticated, isAdmin } = useAuth();
  
  // User and conversation state
  const [user, setUser] = useState(null);
  const [currentConversation, setCurrentConversation] = useState(null);
  
  // Messages state
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [oldestTimestamp, setOldestTimestamp] = useState(null);
  
  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // UI state
  const [includeDeleted, setIncludeDeleted] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('includeDeleted') === 'true';
  });

  useEffect(() => {
    loadUserAndConversation();
  }, [userId, conversationId]);

  useEffect(() => {
    if (currentConversation) {
      loadMessages(conversationId, true);
    }
  }, [includeDeleted]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getAuthHeaders = () => {
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('auth_token='))
      ?.split('=')[1];
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadUserAndConversation = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load user details
      const userResponse = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
        headers: getAuthHeaders()
      });

      if (!userResponse.ok) {
        throw new Error(`Failed to load user: ${userResponse.statusText}`);
      }

      const userData = await userResponse.json();
      setUser(userData.user);

      // Load conversation details
      const conversationResponse = await fetch(`${API_BASE_URL}/admin/conversations/${conversationId}`, {
        headers: getAuthHeaders()
      });

      if (!conversationResponse.ok) {
        throw new Error(`Failed to load conversation: ${conversationResponse.statusText}`);
      }

      const conversationData = await conversationResponse.json();
      setCurrentConversation(conversationData.conversation);

      // Load messages for this conversation
      await loadMessages(conversationId, true);

    } catch (err) {
      console.error('Error loading user and conversation:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (convId, reset = false) => {
    if (!convId) return;
    
    try {
      setMessagesLoading(true);

      const params = new URLSearchParams({ limit: '50', includeDeleted: includeDeleted.toString() });
      if (!reset && oldestTimestamp) {
        params.append('before', oldestTimestamp.toString());
      }

      const response = await fetch(`${API_BASE_URL}/admin/conversations/${convId}/messages?${params}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to load messages: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (reset) {
        setMessages(data.messages || []);
      } else {
        setMessages(prev => [...(data.messages || []), ...prev]);
      }
      
      setHasMore(data.hasMore || false);
      setOldestTimestamp(data.oldestTimestamp || null);

    } catch (err) {
      console.error('Error loading messages:', err);
      setError(err.message);
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleDeleteConversation = async (permanent = false) => {
    if (!conversationId || !currentConversation) return;
    
    const title = currentConversation?.title || 'this conversation';
    
    // Check if trying to soft delete an already deleted conversation
    if (!permanent && currentConversation?.deleted) {
      alert('This conversation is already soft deleted. Use hard delete to permanently remove it.');
      return;
    }
    
    let confirmed;
    if (permanent) {
      confirmed = window.confirm(
        `⚠️ PERMANENT DELETION WARNING ⚠️\n\n` +
        `Are you sure you want to PERMANENTLY DELETE "${title}"?\n\n` +
        `This will completely remove the conversation and all messages from the database.\n` +
        `This action CANNOT be undone.`
      );
    } else {
      confirmed = window.confirm(
        `Are you sure you want to soft delete "${title}"?\n\n` +
        `This will hide the conversation from the user but preserve all data.\n` +
        `It can be restored later if needed.`
      );
    }
    
    if (!confirmed) {
      return;
    }

    try {
      const endpoint = permanent 
        ? `${API_BASE_URL}/admin/conversations/${conversationId}/permanent`
        : `${API_BASE_URL}/admin/conversations/${conversationId}`;
        
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to delete conversation: ${response.statusText}`);
      }

      // Track chat deletion
      trackDeletedChat();

      // Navigate back to conversations list
      window.history.back();

    } catch (err) {
      console.error('Error deleting conversation:', err);
      setError(err.message);
    }
  };

  const exportConversation = () => {
    if (!conversationId || !currentConversation) return;
    
    const exportData = {
      exportedAt: new Date().toISOString(),
      user: {
        userId: user.userId,
        email: user.email
      },
      conversation: {
        conversationId: currentConversation.conversationId,
        title: currentConversation.title,
        createdAt: currentConversation.createdAt,
        lastMessageAt: currentConversation.lastMessageAt,
        model: currentConversation.model
      },
      messages: messages.map(msg => ({
        messageId: msg.messageId,
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp,
        model: msg.model
      })),
      metadata: {
        totalMessages: messages.length,
        exportedBy: 'admin',
        exportFormat: 'json'
      }
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `conversation-${conversationId}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  // Authentication checks
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="mobile-conversation-viewer">
        <div className="mobile-header">
          <Link to={`/admin/users/${userId}/conversations${includeDeleted ? '?includeDeleted=true' : ''}`} className="back-button">
            ← Back
          </Link>
          <h2>Loading...</h2>
        </div>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading conversation...</p>
        </div>
      </div>
    );
  }

  if (error && !currentConversation) {
    return (
      <div className="mobile-conversation-viewer">
        <div className="mobile-header">
          <Link to={`/admin/users/${userId}/conversations${includeDeleted ? '?includeDeleted=true' : ''}`} className="back-button">
            ← Back
          </Link>
          <h2>Error</h2>
        </div>
        <div className="error-state">
          <h3>⚠️ Error Loading Conversation</h3>
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={loadUserAndConversation} className="retry-button">
              🔄 Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-conversation-viewer">
      {/* Mobile Header */}
      <div className="mobile-header">
        <Link to={`/admin/users/${userId}/conversations${includeDeleted ? '?includeDeleted=true' : ''}`} className="back-button">
          ← Back
        </Link>
        <div className="header-info">
          <h2>{currentConversation?.title || 'Untitled Conversation'}</h2>
          <p className="user-info">{user?.email}</p>
        </div>
        <div className="header-actions">
          <button className="mobile-menu-button" onClick={() => {
            const menu = document.querySelector('.mobile-action-menu');
            menu.classList.toggle('open');
          }}>
            ⋮
          </button>
        </div>
      </div>

      {/* Mobile Action Menu - moved outside header */}
      <div className="mobile-action-menu">
          <button onClick={exportConversation} className="menu-item">
            📥 Export
          </button>
          <label className="menu-item checkbox-item">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => {
                setIncludeDeleted(e.target.checked);
                const url = new URL(window.location);
                if (e.target.checked) {
                  url.searchParams.set('includeDeleted', 'true');
                } else {
                  url.searchParams.delete('includeDeleted');
                }
                window.history.replaceState({}, '', url);
              }}
            />
            Show deleted messages
          </label>
          <button 
            onClick={() => handleDeleteConversation(false)}
            className="menu-item delete-item"
            disabled={currentConversation?.deleted}
          >
            🗑️ Soft Delete
          </button>
          <button 
            onClick={() => handleDeleteConversation(true)}
            className="menu-item delete-item danger"
          >
            ⚠️ Hard Delete
          </button>
      </div>

      {/* Content wrapper */}
      <div className="mobile-content-wrapper">
        {error && (
          <div className="error-message">
            <span>⚠️ {error}</span>
            <button onClick={() => setError(null)}>✕</button>
          </div>
        )}

        {/* Conversation Info */}
        {currentConversation && (
        <div className="mobile-conversation-info">
          <div className="info-row">
            <span>Created: {formatTimestamp(currentConversation.createdAt)}</span>
          </div>
          <div className="info-row">
            <span>Last Activity: {formatTimestamp(currentConversation.lastMessageAt)}</span>
          </div>
          <div className="info-row">
            <span>Messages: {messages.length}</span>
            {includeDeleted && messages.filter(m => m.deleted).length > 0 && (
              <span className="deleted-count">
                🗑️ Deleted: {messages.filter(m => m.deleted).length}
              </span>
            )}
          </div>
          {currentConversation.model && (
            <div className="info-row">
              <span>Model: {currentConversation.model}</span>
            </div>
          )}
        </div>
      )}

      {/* Messages Container */}
      <div className="mobile-messages-container">
        {hasMore && (
          <div className="load-more-container">
            <button 
              onClick={() => loadMessages(conversationId, false)}
              disabled={messagesLoading}
              className="load-more-button"
            >
              {messagesLoading ? 'Loading...' : 'Load Earlier Messages'}
            </button>
          </div>
        )}

        <div className="mobile-messages-list">
          {messages.length === 0 ? (
            <div className="empty-messages">
              <p>No messages in this conversation.</p>
            </div>
          ) : (
            messages.map(message => (
              <div key={message.messageId} className={`mobile-message ${message.role} ${message.deleted ? 'deleted' : ''}`}>
                <div className="message-header">
                  <span className="message-role">
                    {message.role === 'user' ? '👤 User' : 
                     message.role === 'system' ? '⚙️ System' : '🤖 Assistant'}
                  </span>
                  <span className="message-timestamp">
                    {formatTimestamp(message.timestamp)}
                  </span>
                  {message.deleted && (
                    <span className="deleted-badge">🗑️</span>
                  )}
                </div>
                {message.model && (
                  <div className="message-model">
                    {message.model}
                  </div>
                )}
                <div className="message-content">
                  {message.content}
                </div>
                {message.deleted && message.deletedAt && (
                  <div className="message-deleted-info">
                    Deleted: {formatTimestamp(message.deletedAt)}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
        <div ref={messagesEndRef} />
      </div>
      </div>
    </div>
  );
};

export default MobileConversationViewer;